import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { MainLayout } from "@/components/layout/MainLayout";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Shield, AlertTriangle, Eye, Clock, Activity, Lock, FileText, Download
} from "lucide-react";
import * as XLSX from "xlsx";
import { downloadBlob } from "@/utils/download";

const severityColors = {
  info: "bg-blue-100 text-blue-700",
  warning: "bg-amber-100 text-amber-700",
  critical: "bg-red-100 text-red-700",
};

const actionLabels = {
  login_success: "Login OK",
  login_failed: "Login falhou",
  login_blocked: "Login bloqueado",
  register: "Registro",
  password_change: "Senha alterada",
  settings_change: "Config. alterada",
  financial_create: "Fatura criada",
  financial_update: "Fatura editada",
  financial_delete: "Fatura eliminada",
  financial_pay: "Fatura paga",
  financial_access: "Acesso financeiro",
  data_create: "Dados criados",
  data_update: "Dados alterados",
  data_delete: "Dados eliminados",
  data_export: "Exportação",
  token_generate: "Token gerado",
  permission_change: "Permissão alterada",
};

export default function AuditPage() {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState("");
  const [filterSeverity, setFilterSeverity] = useState("");
  const [startDate, setStartDate] = useState(new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState(new Date().toISOString().slice(0, 10));

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let url = `/audit/logs?start_date=${startDate}&end_date=${endDate}&limit=200`;
      if (filterAction) url += `&action=${filterAction}`;
      if (filterSeverity) url += `&severity=${filterSeverity}`;

      const [logsRes, statsRes] = await Promise.all([
        authAxiosRef.current.get(url),
        authAxiosRef.current.get("/audit/stats"),
      ]);
      setLogs(logsRes.data);
      setStats(statsRes.data);
    } catch {} finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterAction, filterSeverity, startDate, endDate]);
  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCSV = () => {
    const rows = logs.map(l => ({
      Data: l.timestamp?.slice(0, 19)?.replace("T", " "),
      Ação: actionLabels[l.action] || l.action,
      Severidade: l.severity,
      Detalhes: l.details,
      IP: l.ip || "",
      Recurso: l.resource_type || "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Audit Logs");
    const buf = XLSX.write(wb, { bookType: "csv", type: "array" });
    downloadBlob(new Blob([buf], { type: "text/csv;charset=utf-8" }), `audit_logs_${endDate}.csv`);
  };

  if (loading) return <MainLayout><LoadingScreen /></MainLayout>;

  return (
    <MainLayout>
      <div className="space-y-6" data-testid="audit-page">
        <div>
          <h1 className="text-4xl font-bold uppercase tracking-tight flex items-center gap-3">
            <Shield className="w-8 h-8 text-emerald-600" />Auditoria & Segurança
          </h1>
          <p className="text-muted-foreground mt-1">Logs de todas as ações do sistema (compliance LGPD/GDPR)</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="bg-card border-border/50"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground font-medium mb-1">Total Registros</p>
              <span className="text-2xl font-black">{stats.total}</span>
            </CardContent></Card>
            <Card className="bg-card border-border/50"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground font-medium mb-1">Últimas 24h</p>
              <span className="text-2xl font-black text-blue-600">{stats.last_24h}</span>
            </CardContent></Card>
            <Card className="bg-card border-border/50"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground font-medium mb-1">Últimos 7 dias</p>
              <span className="text-2xl font-black">{stats.last_7d}</span>
            </CardContent></Card>
            <Card className="bg-card border-border/50"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground font-medium mb-1">Avisos (7d)</p>
              <span className="text-2xl font-black text-amber-600">{stats.warnings_7d}</span>
            </CardContent></Card>
            <Card className="bg-card border-border/50"><CardContent className="p-4">
              <p className="text-xs text-muted-foreground font-medium mb-1">Login falhos (24h)</p>
              <span className="text-2xl font-black text-red-600">{stats.login_failures_24h}</span>
            </CardContent></Card>
          </div>
        )}

        {/* Top Actions */}
        {stats?.top_actions?.length > 0 && (
          <Card className="border-border/50">
            <CardContent className="p-4">
              <p className="text-xs font-bold text-muted-foreground uppercase mb-3">Ações mais frequentes (7 dias)</p>
              <div className="flex flex-wrap gap-2">
                {stats.top_actions.map(a => (
                  <Badge key={a.action} variant="outline" className="text-xs gap-1">
                    <Activity className="w-3 h-3" />{actionLabels[a.action] || a.action}: {a.count}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2 items-end">
          <div className="flex-1 grid grid-cols-4 gap-2">
            <div>
              <Label className="text-xs">Ação</Label>
              <Select value={filterAction} onValueChange={setFilterAction}>
                <SelectTrigger data-testid="filter-action"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="login">Logins</SelectItem>
                  <SelectItem value="financial">Financeiro</SelectItem>
                  <SelectItem value="data">Dados</SelectItem>
                  <SelectItem value="settings">Configurações</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Severidade</Label>
              <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                <SelectTrigger data-testid="filter-severity"><SelectValue placeholder="Todas" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="critical">Critical</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">De</Label><Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} /></div>
            <div><Label className="text-xs">Até</Label><Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} /></div>
          </div>
          <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={exportCSV} data-testid="export-audit-csv"><Download className="w-3 h-3" />CSV</Button>
        </div>

        {/* Logs Table */}
        <div className="bg-card rounded-lg border border-border/50 overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border/50 text-muted-foreground text-xs uppercase">
              <th className="text-left p-3 font-medium w-40">Data/Hora</th>
              <th className="text-left p-3 font-medium">Ação</th>
              <th className="text-left p-3 font-medium">Detalhes</th>
              <th className="text-center p-3 font-medium w-20">Nível</th>
              <th className="text-left p-3 font-medium w-28 hidden md:table-cell">IP</th>
            </tr></thead>
            <tbody className="divide-y divide-border/30">
              {logs.map(l => (
                <tr key={l.id} className="hover:bg-muted/50" data-testid={`log-row-${l.id?.slice(-6)}`}>
                  <td className="p-3 text-muted-foreground text-xs font-mono">
                    {l.timestamp ? new Date(l.timestamp).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "-"}
                  </td>
                  <td className="p-3">
                    <span className="font-medium text-xs">{actionLabels[l.action] || l.action}</span>
                    {l.resource_type && <span className="text-[10px] text-muted-foreground ml-1">({l.resource_type})</span>}
                  </td>
                  <td className="p-3 text-muted-foreground text-xs max-w-xs truncate">{l.details}</td>
                  <td className="p-3 text-center">
                    <Badge className={`text-[10px] ${severityColors[l.severity] || "bg-gray-100 text-gray-700"}`}>
                      {l.severity}
                    </Badge>
                  </td>
                  <td className="p-3 text-muted-foreground text-xs font-mono hidden md:table-cell">{l.ip || "-"}</td>
                </tr>
              ))}
              {logs.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Nenhum registro encontrado</td></tr>}
            </tbody>
          </table>
        </div>
        <p className="text-xs text-muted-foreground">{logs.length} registros exibidos</p>
      </div>
    </MainLayout>
  );
}
